import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import type { ColDef } from "ag-grid-community";
import { themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import {
  ITrustedSecretaryMetrics,
  ITrustedSecretaryReferralRow,
} from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";
import { helperService } from "src/services/helper.service";
import { apiService } from "src/services/api.service";
import { toast } from "src/components/Toast/ToastManager";
import styles from "./TrustedSecretary.module.scss";

interface IRow {
  club_name: string;
  city: string;
  state: string;
  country: string;
  date_referred: string;
  commission: string;
}

const TrustedSecretary: React.FC = () => {
  const navigate = useNavigate();
  const user = helperService.getUser();
  const [rows, setRows] = useState<IRow[]>([]);
  const [metrics, setMetrics] = useState<ITrustedSecretaryMetrics>({
    clubs: 0,
    events: 0,
    commission: 0,
  });
  const [loading, setLoading] = useState<boolean>(true);

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    []
  );

  const formatDateWithoutTimezoneShift = (value: Date | string): string => {
    const iso = typeof value === "string" ? value : value.toISOString();
    const [year, month, day] = iso.split("T")[0].split("-");
    return `${Number(month)}/${Number(day)}/${year}`;
  };

  useEffect(() => {
    if (
      user.user_type !== Constants.user_type.owner ||
      !user.is_trusted_secretary
    ) {
      navigate("/account");
      return;
    }

    const load = async () => {
      try {
        const [referrals, summary] = await Promise.all([
          apiService.getTrustedSecretaryReferrals(),
          apiService.getTrustedSecretaryMetrics(),
        ]);

        setRows(
          (referrals || []).map((ref: ITrustedSecretaryReferralRow) => ({
            club_name: ref.club_name,
            city: ref.city || "—",
            state: ref.state || "—",
            country: ref.country || "—",
            date_referred: formatDateWithoutTimezoneShift(ref.date_referred),
            commission: currencyFormatter.format(ref.commission || 0),
          }))
        );

        setMetrics(summary);
      } catch (error) {
        toast.show({
          title: "Trusted Secretary",
          content: "Unable to load trusted secretary dashboard data.",
          duration: 10000,
          type: "fail",
          errorDetails: error as any,
        });
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [currencyFormatter, navigate, user]);

  const colDefs: ColDef<IRow>[] = [
    { field: "club_name", headerName: "Club Name", flex: 2, minWidth: 200 },
    { field: "city", headerName: "City", minWidth: 120, valueFormatter: (p) => p.value || "—" },
    { field: "state", headerName: "State", minWidth: 90, valueFormatter: (p) => p.value || "—" },
    { field: "country", headerName: "Country", minWidth: 100, valueFormatter: (p) => p.value || "—" },
    { field: "date_referred", headerName: "Date Referred", minWidth: 150 },
    { field: "commission", headerName: "Commission", minWidth: 150 },
  ];

  const theme = themeQuartz.withParams({
    fontFamily: "Roboto",
    headerFontFamily: "Roboto",
    cellFontFamily: "Roboto",
  });

  return (
    <div className={styles.TrustedSecretary}>
      <Helmet>
        <title>Trusted Secretary Dashboard</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className={styles.inner}>
        <div className={styles.breadcrumb}>
          <div className={styles.path}>
            <Link to="/account">Account</Link> /
          </div>
          <div className={styles.current}>Trusted Secretary</div>
        </div>
        <h1>Trusted Secretary Dashboard</h1>

        <div className={styles.metrics}>
          <div className={styles.metricItem}>
            <div className={styles.num}>{metrics.clubs || 0}</div>
            <div className={styles.label}># of Clubs</div>
          </div>
          <div className={styles.metricItem}>
            <div className={styles.num}>{metrics.events || 0}</div>
            <div className={styles.label}># of Events</div>
          </div>
          <div className={styles.metricItem}>
            <div className={styles.num}>
              {currencyFormatter.format(metrics.commission || 0)}
            </div>
            <div className={styles.label}>Commission</div>
          </div>
        </div>

        <div className={styles.tableWrap}>
          {loading ? (
            <div className={styles.emptyState}>Loading referrals...</div>
          ) : rows.length === 0 ? (
            <div className={styles.emptyState}>
              No referred clubs yet. Once clubs are added for your account, they will appear here.
            </div>
          ) : (
            <div className={styles.agGridTheme}>
              <AgGridReact
                theme={theme}
                rowData={rows}
                columnDefs={colDefs}
                rowHeight={55}
                headerHeight={50}
                animateRows={true}
                domLayout="autoHeight"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrustedSecretary;
